/**
 * Provenance behavior — what made this scene
 *
 * Data-only and completely inert: no attach work, nothing rendered, nothing
 * loaded, no runtime reads it. It is here so a finished scene can answer a
 * question the format otherwise cannot — "where did you come from" — which is
 * what remixing, attribution and repeatable generation runs all need.
 *
 * Where it attaches follows what it describes. Generation provenance belongs on
 * the **scene root**, because that is the one place a behavior lands in both
 * homes at once: serialized into the `.trace` archive's `scene.json` (see
 * `export/TraceExport.ts`) and stored on the CMS asset record, since scene root
 * behaviors *are* the `metadata` payload (see `editor/PublishDecision.ts`).
 * The second half is the point — it makes generated scenes searchable by prompt
 * or template without downloading a byte of archive.
 *
 * Asset provenance — what an individual object's `src` was derived from, such
 * as the authored source behind a publish-time bake — attaches to the object it
 * describes. A per-object record deliberately gets only the first home: it
 * travels in the file next to the `src` it explains, and stays out of the CMS
 * metadata payload, which carries root behaviors only.
 *
 * Not `cmsManaged`, unlike {@link ImageAnchor}: this must travel inside the
 * file too. A `.trace` that a creator downloads and re-opens elsewhere should
 * still know what made it.
 */

import { TraceBehavior } from "./tracebehavior";
import type { ProvenanceData } from "../scenedata/behaviors/provenancedata";

export class Provenance extends TraceBehavior<ProvenanceData> {
    static readonly behaviorType = "Provenance" as const;

    /**
     * Provenance data is text a person typed, not authored bindings.
     *
     * A prompt like `a card with {glow} lighting` or `the $5 tier` contains
     * exactly the syntax the base class treats as a property reference. Left
     * alone, the resolver would subscribe this behavior to properties named
     * after words in someone's sentence and substitute theme values into the
     * record of what they asked for. `serialize()` returns authored data, so
     * the file would survive it — but the in-memory record would quietly stop
     * matching what the creator wrote, which is the one thing this behavior
     * exists to preserve.
     */
    static readonly literalData = true;
}
